// src/state/playSettingsState.js
export const PLAY_SETTINGS_STORAGE_KEY = 'app_play_settings';
export const PLAY_SETTINGS_CHANGED_EVENT = 'app_play_settings_changed';

export const DEFAULT_PLAY_SETTINGS = Object.freeze({
  muckEnabled: false,
  autoRebuyEnabled: false,
  autoRebuyBb: 100,
  uiSizeScale: 1,
});

// Shared by desktop/mobile lobby and all table windows. Invalid old values fall back to 1x.
export const UI_SIZE_SCALES = Object.freeze([1, 1.1, 1.2]);

export function normalizeUiSizeScale(value) {
  const n = Number(value);
  return UI_SIZE_SCALES.includes(n) ? n : 1;
}

export const AUTO_REBUY_BB_OPTIONS = Object.freeze([100, 200, 400]);

export function normalizeAutoRebuyBb(value) {
  const n = Number(value);
  return AUTO_REBUY_BB_OPTIONS.includes(n) ? n : 100;
}

function safeParse(raw) {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function readPlaySettingsSnapshot() {
  try {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_PLAY_SETTINGS };
    const stored = safeParse(localStorage.getItem(PLAY_SETTINGS_STORAGE_KEY));
    return {
      ...DEFAULT_PLAY_SETTINGS,
      ...(stored || {}),
      muckEnabled: stored?.muckEnabled == null ? false : !!stored.muckEnabled,
      autoRebuyEnabled: stored?.autoRebuyEnabled == null ? false : !!stored.autoRebuyEnabled,
      autoRebuyBb: normalizeAutoRebuyBb(stored?.autoRebuyBb),
      uiSizeScale: normalizeUiSizeScale(stored?.uiSizeScale),
    };
  } catch (_) {
    return { ...DEFAULT_PLAY_SETTINGS };
  }
}

export function persistPlaySettings(state) {
  const next = {
    ...DEFAULT_PLAY_SETTINGS,
    ...(state || {}),
    muckEnabled: state?.muckEnabled == null ? false : !!state.muckEnabled,
    autoRebuyEnabled: state?.autoRebuyEnabled == null ? false : !!state.autoRebuyEnabled,
    autoRebuyBb: normalizeAutoRebuyBb(state?.autoRebuyBb),
    uiSizeScale: normalizeUiSizeScale(state?.uiSizeScale),
  };
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PLAY_SETTINGS_STORAGE_KEY, JSON.stringify(next));
    }
    try {
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent(PLAY_SETTINGS_CHANGED_EVENT, { detail: next }));
      }
    } catch (_) {}
  } catch (_) {}
  return next;
}

export function playSettingsReducer(state, action) {
  const current = state || readPlaySettingsSnapshot();
  switch (action?.type) {
    case 'PLAY_SETTINGS/SET_MUCK_ENABLED': {
      return persistPlaySettings({
        ...current,
        muckEnabled: !!action.payload,
      });
    }
    case 'PLAY_SETTINGS/SET_AUTO_REBUY_ENABLED': {
      return persistPlaySettings({
        ...current,
        autoRebuyEnabled: !!action.payload,
      });
    }
    case 'PLAY_SETTINGS/SET_AUTO_REBUY_BB': {
      return persistPlaySettings({
        ...current,
        autoRebuyBb: normalizeAutoRebuyBb(action.payload),
      });
    }
    case 'PLAY_SETTINGS/SET_UI_SIZE_SCALE': {
      return persistPlaySettings({
        ...current,
        uiSizeScale: normalizeUiSizeScale(action.payload),
      });
    }
    case 'PLAY_SETTINGS/REFRESH': {
      return readPlaySettingsSnapshot();
    }
    default:
      return current;
  }
}
